import { useEffect, useState } from "react";
import api from "@/admin/api";
import { Eyebrow } from "@/components/comiq/Eyebrow";
import { COLORS } from "@/comiq/tokens";
import { toast } from "sonner";
import { Download, Table2, BookText, Info, Link2 } from "lucide-react";

export default function Exports() {
  const [waves, setWaves] = useState([]);
  const [waveId, setWaveId] = useState("");
  const [scope, setScope] = useState("wave"); // wave | study
  const [preview, setPreview] = useState({ dataset: "", codebook: "" });
  const [busy, setBusy] = useState("");

  useEffect(() => {
    api.get("/admin/waves").then((r) => {
      setWaves(r.data);
      const pick = r.data.find((w) => w.is_demo && w.status !== "archived") || r.data.find((w) => w.status !== "archived");
      if (pick) setWaveId(pick.wave_id);
    }).catch(() => {});
  }, []);

  useEffect(() => { setPreview({ dataset: "", codebook: "" }); }, [waveId, scope]);

  const wave = waves.find((w) => w.wave_id === waveId);
  const urlFor = (kind) =>
    kind === "dataset" && scope === "study"
      ? `/admin/studies/${wave?.study_id}/export/dataset.csv`
      : `/admin/waves/${waveId}/export/${kind}.csv`;

  const fetchCsv = async (kind) => {
    setBusy(kind);
    try {
      const r = await api.get(urlFor(kind), { responseType: "text" });
      return r.data;
    } catch (e) {
      toast.error(`Gagal mengambil ekspor (HTTP ${e.response?.status ?? "?"}): ${e.response?.data?.detail || e.message}`);
      return null;
    } finally { setBusy(""); }
  };

  const download = async (kind) => {
    const text = await fetchCsv(kind);
    if (text == null) return;
    const label = kind === "dataset" && scope === "study" ? `${wave?.study_id}-lintas-gelombang` : (wave?.label || waveId);
    const blob = new Blob([text], { type: "text/csv" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `${kind}-${String(label).replace(/\s+/g, "_")}.csv`;
    a.click();
    URL.revokeObjectURL(a.href);
    toast.success(`${kind === "dataset" ? "Dataset" : "Codebook"} terunduh`);
  };

  const showPreview = async (kind) => {
    const text = await fetchCsv(kind);
    if (text == null) return;
    setPreview((p) => ({ ...p, [kind]: text.split("\n").slice(0, 6).join("\n") }));
  };

  const card = (kind, title, desc, Icon, extra) => (
    <div className="rounded-comiq border bg-comiq-surface-alt p-5" style={{ borderColor: COLORS.line }} data-testid={`export-card-${kind}`}>
      <Icon size={20} style={{ color: COLORS.orangeInk }} />
      <h3 className="mt-2 text-lg font-semibold" style={{ color: COLORS.ink }}>{title}</h3>
      <p className="mt-1 text-sm" style={{ color: COLORS.textSoft }}>{desc}</p>
      {extra}
      <div className="mt-4 flex gap-2">
        <button onClick={() => download(kind)} disabled={!waveId || busy} className="btn-comiq btn-accent disabled:opacity-40" data-testid={`download-${kind}-btn`}>
          <Download size={15} /> Unduh CSV
        </button>
        <button onClick={() => showPreview(kind)} disabled={!waveId || busy} className="btn-comiq btn-line disabled:opacity-40" data-testid={`preview-${kind}-btn`}>Pratinjau</button>
      </div>
      {preview[kind] && (
        <pre className="mt-3 max-h-56 overflow-auto rounded-comiq-sm border p-3 text-[0.7rem] leading-relaxed" style={{ borderColor: COLORS.line, background: COLORS.paper, color: COLORS.ink }} data-testid={`preview-${kind}`}>{preview[kind]}</pre>
      )}
    </div>
  );

  return (
    <div className="p-6">
      <Eyebrow>Ekspor Data</Eyebrow>
      <h1 className="font-serif-editorial mt-1 text-3xl" style={{ color: COLORS.ink }}>Ekspor dataset &amp; codebook</h1>

      <div className="mt-4 flex items-start gap-2.5 rounded-comiq border p-4 text-sm" style={{ borderColor: "#CDE7D2", background: "#F1FAF3", color: "#15803D" }} data-testid="export-notice">
        <Info size={16} className="mt-0.5 shrink-0" />
        <span>
          Dataset bebas identitas (kode partisipan saja — tanpa nama/email) dan memuat <strong>organization_id + wave_id stabil</strong> untuk
          penautan lintas-gelombang. Kolom <code>flag_*</code> bersifat <strong>advisory</strong> dan <strong>tidak mengecualikan</strong> respons mana pun.
          <strong> Skor &amp; benchmark riset belum diaktifkan</strong> hingga instrumen &amp; aturan final disetujui. Contoh dari data DEMO.
        </span>
      </div>

      <div className="mt-5">
        <select value={waveId} onChange={(e) => setWaveId(e.target.value)} className="rounded-comiq-sm border bg-white px-3 py-2 text-sm outline-none" style={{ borderColor: COLORS.line, color: COLORS.ink }} data-testid="export-wave-select">
          {waves.map((w) => <option key={w.wave_id} value={w.wave_id}>{w.label}</option>)}
        </select>
      </div>

      <div className="mt-6 grid grid-cols-1 gap-4 lg:grid-cols-2">
        {card(
          "dataset", "Dataset responden",
          "Satu baris per sesi terkirim: organization_id + wave_id stabil, nilai per item (angka / 'unsure' / kosong), dan kolom penanda advisory. Bebas PII.",
          Table2,
          <div className="mt-3 flex items-center gap-2 text-xs" data-testid="dataset-scope">
            <Link2 size={13} style={{ color: COLORS.orangeInk }} />
            <span style={{ color: COLORS.textSoft }}>Cakupan:</span>
            <button onClick={() => setScope("wave")} className="rounded-full border px-3 py-1 font-medium" style={{ borderColor: scope === "wave" ? COLORS.orange : COLORS.line, background: scope === "wave" ? "rgba(242,84,27,0.08)" : "transparent", color: scope === "wave" ? COLORS.orangeInk : COLORS.textSoft }} data-testid="scope-wave-btn">Gelombang ini</button>
            <button onClick={() => setScope("study")} className="rounded-full border px-3 py-1 font-medium" style={{ borderColor: scope === "study" ? COLORS.orange : COLORS.line, background: scope === "study" ? "rgba(242,84,27,0.08)" : "transparent", color: scope === "study" ? COLORS.orangeInk : COLORS.textSoft }} data-testid="scope-study-btn">Seluruh gelombang studi</button>
          </div>
        )}
        {card("codebook", "Codebook", "Definisi tiap item: seksi, wording, tipe, skala & anchor, opsi, reverse/scored, versi wording, dan aturan pengkodean nilai.", BookText)}
      </div>
    </div>
  );
}
